import {Outlet} from 'react-router-dom';
import {
  EnvironmentBanner,
  SidebarInset,
  SidebarProvider,
  Toaster
} from '@kroll-resolver-crisp/ui-components-react';
import {AppSidebar} from './AppSidebar';
import {FeedbackJiraToast} from './FeedbackJiraToast';
import {PageHeader, PageHeaderProvider} from './PageHeader';
import {ENVIRONMENT_BANNER_ENV} from '@/lib/platform';
import {cn} from '@/lib/utils';

// `EnvironmentBanner`'s `min-h-7`. The package's `Sidebar` is `fixed inset-y-0 h-svh`, so it ignores
// this flex column and would paint over the banner; AppSidebar offsets itself by this variable.
const SHELL_CLASS = cn(
  'flex h-svh flex-col',
  ENVIRONMENT_BANNER_ENV ? '[--env-banner-height:1.75rem]' : '[--env-banner-height:0px]'
);

const SIDEBAR_STATE_COOKIE = 'sidebar_state';

// `SidebarProvider` writes the collapsed state to a cookie but never reads it back - that design
// assumes a server framework passing `defaultOpen` - so an SPA reads it itself or the sidebar
// reopens on every load. Absent cookie means open.
function readSidebarOpen(): boolean {
  return !document.cookie.split('; ').includes(`${SIDEBAR_STATE_COOKIE}=false`);
}

// The design system's shell: an icon-collapsible sidebar, the mandatory page-title header, and the
// content inset. Under 768px the package swaps the sidebar for a drawer on its own.
export function AppShell() {
  return (
    <PageHeaderProvider>
      <div className={SHELL_CLASS}>
        {/* Non-live environments only, so nobody mistakes them for the real thing. */}
        {ENVIRONMENT_BANNER_ENV ? <EnvironmentBanner env={ENVIRONMENT_BANNER_ENV} /> : null}
        <SidebarProvider defaultOpen={readSidebarOpen()} className="min-h-0 flex-1">
          <AppSidebar />
          <SidebarInset className="min-w-0 overflow-hidden">
            <PageHeader />
            {/* Pages size themselves against this box (`h-full`, their own scroll regions), so it is
                the one element that scrolls by default. */}
            <div className="min-h-0 flex-1 overflow-auto">
              <Outlet />
            </div>
          </SidebarInset>
        </SidebarProvider>
      </div>
      {/* Sits on z-toast, above sheets and dialogs. FeedbackJiraToast must follow it: see there. */}
      <Toaster />
      <FeedbackJiraToast />
    </PageHeaderProvider>
  );
}
